import { Location } from '@angular/common'
import { Component, HostBinding, Injectable, OnInit, ViewChild, ChangeDetectorRef, signal, OnDestroy, effect, computed, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { ToastrService } from 'ngx-toastr'
import { combineLatest, Subscription } from 'rxjs'
import { VetcorPricingTableComponent } from '../vetcor-pricing-table/vetcor-pricing-table.component'
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import {
   PricingManagerClient,
   VetcorPricingCaptainDto,
   ApplicationArea,
   AuthApiClient,
   AppUser,
   PracticeBatchInfo,
} from 'src/app/shared/services/ppv.api'
import { ComponentStore } from '@ngrx/component-store'
import { Validators, FormBuilder, FormGroup } from '@angular/forms'
import { MatStepper } from '@angular/material/stepper'
import * as datefns from 'date-fns'
import { HospitalPricingService } from '../hospital-pricing.service'
import { CalendlySchedulerComponent } from '../calendly-scheduler/calendly-scheduler.component'
import { BsModalService } from 'ngx-bootstrap/modal'
import { VetcorCoverSheetModalComponent } from './coversheet-modal/coversheet-modal.component'

@Injectable()
export class HospitalPricingComponentStore extends ComponentStore<{
   loadingPIMS: boolean
   searchText: string
   searchCode: string
   selectedCategory: string
}> {
   constructor() {
      super({
         loadingPIMS: false,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })
   }
}

@Component({
   selector: 'vc-hospital-pricing',
   templateUrl: './hospital-pricing.component.html',
   styleUrls: ['./hospital-pricing.component.scss'],
   providers: [HospitalPricingComponentStore],
   standalone: false,
})
export class HospitalPricingComponent implements OnInit, OnDestroy {
   @ViewChild(VetcorPricingTableComponent)
   childTable!: VetcorPricingTableComponent

   pricingService = inject(HospitalPricingService)

   hospitalPrices = this.pricingService.hospitalPrices

   currentUser = signal<AppUser>(undefined)
   selectedPracticeSKey = signal<number>(undefined)
   selectedPractice = computed(() => this.currentUser()?.practices.find((f) => f.id === this.selectedPracticeSKey()))
   calendlySummary = ''

   selectedCategory = ''
   products: any[] = []

   pendingRows = computed(() => []) // todo - probably put something like isPending onto PRicingRowVM
   loadingSub = computed(() => this.hospitalPrices === undefined)
   dataSource: MatTableDataSource<PricingManagerClient> = new MatTableDataSource()
   @ViewChild(MatSort) sort!: MatSort
   public filteredProducts: Array<any> = []
   loadingNewPractice = false

   currentStepIndex = 0
   firstFormGroup: FormGroup
   coverLetterDisplay = true
   approveAllPrices = false
   siteOptions: any[] = []
   selectedSite: any = null

   public pricingCaptain = this.pricingService.pricingCaptain

   public codeFilterValue: string = ''
   public productNameFilter: string = ''
   public productTypeFilter: string = ''
   public productCategoryFilter: string = ''
   @ViewChild(MatStepper) stepper!: MatStepper

   showNewCodeDialog = signal<boolean>(false)

   isAdmin = signal<boolean>(false)
   isReadOnly = computed(() => !this.isAdmin() && this.hasReadOnlyRole())
   hasReadOnlyRole = signal<boolean>(false)
   activeCycle = this.pricingService.activeCycle

   subscriptions: Subscription[] = []

   pricingChangeRequestClinics: number[] = []

   get firstStepCompleted(): boolean {
      return this.firstFormGroup.valid
   }

   state$ = combineLatest([this.store.select((s) => s)]).pipe(
      map(([state]) => ({
         ...state,
      }))
   )

   isCaptain = false
   public productCategories: Array<{ label: string; value: string | null }> = []

   @ViewChild(CalendlySchedulerComponent) calendly: CalendlySchedulerComponent
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   constructor(
      private authManager: AuthManager,
      private route: ActivatedRoute,
      private location: Location,
      private store: HospitalPricingComponentStore,
      private pricingManagerClient: PricingManagerClient,
      private _formBuilder: FormBuilder,
      private cdr: ChangeDetectorRef,
      private authApiClient: AuthApiClient,
      private toastr: ToastrService,
      private bsModalService: BsModalService,
      public router: Router
   ) {
      this.subscriptions.push(
         this.route.paramMap
            .pipe(
               map((pm) => Number(pm.get('practiceSKey'))),
               distinctUntilChanged(),
               tap((practiceSKey) => {
                  this.selectedPracticeSKey.set(practiceSKey)

                  if (practiceSKey) {
                     this.loadingNewPractice = true
                     this.hospitalPrices.set(undefined)
                     this.pricingService.loadHospitalPrices(practiceSKey)
                  } else {
                     this.location.go('/vetcor-pricing/hospital-pricing')
                  }
               })
            )
            .subscribe()
      )

      effect(() => {
         const prices = this.hospitalPrices()
         if (prices !== undefined && prices !== null) {
            this.loadingNewPractice = false
         }
      })

      if (!!this.route.snapshot.queryParamMap.get('gotoadmin')) {
         this.subscriptions.push(
            this.authManager.getCurrentUser().subscribe((user) => {
               let isPricingManagerAdmin = this.authManager.canAccess(user, {
                  application: ApplicationArea.PricingManager,
                  roleName: ['PricingManagerCaptain'],
               })

               if (isPricingManagerAdmin) {
                  this.router.navigateByUrl('/vetcor-pricing/admin-requests')
               }
            })
         )
      }
   }

   ngOnInit(): void {
      this.store.setState({
         loadingPIMS: false,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })

      this.firstFormGroup = this._formBuilder.group({
         firstCtrl: ['', Validators.required],
      })

      this.subscriptions.push(
         this.authApiClient
            .getActivePractices(ApplicationArea.PricingChangeRequest)
            .pipe(
               take(1),
               tap((practices) => {
                  this.pricingChangeRequestClinics = practices
               })
            )
            .subscribe()
      )

      this.subscriptions.push(
         this.authManager
            .getCurrentUser()
            .pipe(
               take(1),
               tap((user) => {
                  //get admin role
                  this.isAdmin.set(false)
                  this.hasReadOnlyRole.set(false)

                  this.currentUser.set(user)
                  user.roles.forEach((role) => {
                     if (role.roleName == 'PricingManagerAdmin') {
                        this.isAdmin.set(true)
                     }

                     if (role.roleName == 'PricingManagerReadOnly') {
                        this.hasReadOnlyRole.set(true)
                     }
                  })
               })
            )
            .subscribe()
      )
   }

   ngOnDestroy(): void {
      if (this.subscriptions.length > 0) {
         this.subscriptions.forEach((sub) => sub.unsubscribe())
      }
   }

   onEventScheduled(event: { startTime: Date; endTime: Date; calendlyId: string }) {
      const formattedDate = new Intl.DateTimeFormat('en-US', {
         weekday: 'long',
         year: 'numeric',
         month: 'long',
         day: 'numeric',
      }).format(event.startTime)
      const formattedStartTime = new Intl.DateTimeFormat('en-US', {
         hour: 'numeric',
         minute: 'numeric',
         hour12: true,
      }).format(event.startTime)
      const formattedEndTime = new Intl.DateTimeFormat('en-US', {
         hour: 'numeric',
         minute: 'numeric',
         hour12: true,
      }).format(event.endTime)

      this.stepper.next()

      this.calendlySummary = `${formattedDate}<br>${formattedStartTime} - ${formattedEndTime}`

      this.subscriptions.push(
         this.pricingManagerClient
            .submitPricingUpdates({
               batchId: this.activeCycle()?.batchId,
               calendlyId: event.calendlyId,
            })
            .subscribe()
      )
   }

   proceedToStep2(stepper: MatStepper) {
      if (this.childTable.areAllInputsValid()) {
         //this.calendly.initCalendlyWidget()
         stepper.next()
      } else {
         this.findFirstInvalidInputAndFocus()
      }
   }

   findFirstInvalidInputAndFocus(): void {
      this.childTable.findFirstInvalidInputAndFocus()
   }

   skipToStep3(stepper: MatStepper) {
      setTimeout(() => document.getElementById('sureModalButton')?.click(), 100)
   }

   confirmStep3() {
      this.approveAllPrices = true
      this.stepper.next()
      this.stepper.next()
      this.subscriptions.push(this.pricingManagerClient.submitPricingUpdates({ batchId: this.activeCycle()?.batchId, calendlyId: '' }).subscribe())
   }

   onBookingFinish(stepper: MatStepper) {
      stepper.previous()
      stepper.previous()
   }

   scrollUp(): void {
      ;(window as any).scrollTo({ top: 0, behavior: 'smooth' })
   }

   exportTablePDF() {
      setTimeout(() => {
         if (this.childTable) {
            this.childTable.exportTableToPDF('practice', this.filteredProducts)
         }
      }, 200)
   }

   isValidPrice(value: any): boolean {
      const regex = /^\d+(\.\d+)?$/
      return regex.test(value?.toString())
   }

   onRecordUpdated(): void {
      this.cdr.detectChanges()
   }

   showCoverSheetModal(): void {
      this.bsModalService.show(VetcorCoverSheetModalComponent, {
         class: 'modal-xl custom-modal pricing-big-modal modal-padding',
         backdrop: 'static',
         initialState: {
            practiceSKey: this.selectedPracticeSKey(),
            activeCycle: this.activeCycle,
            pricingCaptain: this.pricingCaptain,
         },
      })
   }
}
